import React from "react";
import Input from "../input";
import Icon from "assets/icons";
import classNames from "classnames";
import Button from "shared/components/button";
import { useCopies } from "shared/hooks/useCopies";
import useOnOutSideClick from "shared/hooks/useOnOutSideClick";
import { CustomSelectOptionType, OptionsDropdownListType, CustomSelectDropdownConfig } from "../types";

function CustomSelectDropdown({ onChange, ...props }: CustomSelectDropdownConfig) {
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const [showDropdown, setShowDropdown] = React.useState<boolean>(false);
  const [option, setOption] = React.useState<CustomSelectOptionType>(props.value || props.options[0]);

  React.useEffect(() => {
    if (showDropdown) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [showDropdown]);

  const toggleDropdown = React.useCallback(() => {
    setShowDropdown(val => !val);
  }, []);

  const onOptionSelectHandler = React.useCallback(
    opt => {
      setOption(opt);
      setShowDropdown(false);
      onChange?.(opt);
    },
    [onChange]
  );

  useOnOutSideClick({
    node: dropdownRef.current,
    onOutSideClick: () => {
      setShowDropdown(false);
    }
  });

  return (
    <div
      ref={dropdownRef}
      className={classNames("customSelect_dropdown_wrapper customSelect_dropdown", {
        "customSelect_dropdown_wrapper--disabled": props.disabled
      })}>
      <div
        onClick={toggleDropdown}
        className={classNames("customSelect_dropdown_selectedOption_details", {
          isDropdownOpen: showDropdown
        })}
        dangerouslySetInnerHTML={{ __html: option.selectedLabel }}
      />
      <div
        className={classNames("customSelect_dropdown_list_wrapper", {
          showDropdown: showDropdown
        })}>
        <div className="xsVisible customSelect_dropdown_list_header">
          <b>{props.label}</b>
          <span className="customSelect_dropdown_list_close" onClick={toggleDropdown}>
            <Icon.Close />
          </span>
        </div>
        {showDropdown && (
          <GetDropdownList
            name={props.name}
            options={props.options}
            selectedOption={option}
            showDropdown={showDropdown}
            placeholder={props.placeholder}
            onChange={onOptionSelectHandler}
          />
        )}
      </div>
      <div
        onClick={toggleDropdown}
        className={classNames("customSelect_dropdown_overlay smooth xsVisible", {
          show: showDropdown
        })}
      />
    </div>
  );
}

function GetDropdownList(props: OptionsDropdownListType) {
  const copies = useCopies();
  const inputRef = React.useRef<any>();
  const [showIcon, setShowIcon] = React.useState<Boolean>(false);
  const [filteredOptions, setFilteredOptions] = React.useState(props.options);

  const searchOptions = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const keyword = e.target.value;
      if (keyword !== "") {
        setShowIcon(true);
        const searchResult = props.options.filter(option => {
          return ((option.searchFieldValue || option.label) as string).toLowerCase().includes(keyword.toLowerCase());
        });
        setFilteredOptions(searchResult);
      } else {
        setShowIcon(false);
        setFilteredOptions(props.options);
      }
    },
    [props.options]
  );

  const clearSearch = React.useCallback(() => {
    if (showIcon) {
      setFilteredOptions(props.options);
      setShowIcon(false);
      inputRef.current.childNodes[0].firstChild?.focus();
      inputRef.current.childNodes[0].firstChild.value = "";
    }
  }, [props.options, showIcon]);

  return (
    <div className="customSelect_dropdown_list">
      <div className="customSelect_dropdown_list_search" ref={inputRef}>
        <Input
          autoFocus
          name={props.name}
          switchIconPosition
          onBlur={props.onBlur}
          onChange={searchOptions}
          disabled={props.disabled}
          hasError={props.hasError}
          placeholder={props.placeholder}
          autoComplete="off"
          icon={
            <Button
              onClick={clearSearch}
              icon={!showIcon ? <Icon.Search /> : <Icon.Close />}
              className={classNames("customSelect_dropdown_list_search_icon", {
                "customSelect_dropdown_list_search_icon--clearSearch": showIcon
              })}
            />
          }
        />
      </div>
      <ul className="customSelect_dropdown_optionsList list-unstyled">
        {filteredOptions.length > 0 ? (
          filteredOptions.map((optionObj, idx) => {
            return (
              <li key={idx}>
                <div
                  className={classNames("customSelect_dropdown_optionsList_item smooth", {
                    active: props.selectedOption.value === optionObj.value
                  })}
                  onClick={() => props.onChange(optionObj)}
                  dangerouslySetInnerHTML={{ __html: optionObj.label as string }}
                />
              </li>
            );
          })
        ) : (
          <p className="customSelect_dropdown_optionsList_noResults">{copies.get("no.results.found")}</p>
        )}
      </ul>
    </div>
  );
}

export default CustomSelectDropdown;
